import { useState, type MouseEvent } from 'react';
import { AlertTriangle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  describeAccountUnavailable,
  ESCALATE_TO_ADMIN_SUFFIX,
  FALLBACK_ATTENTION_LABEL,
  isBanStormHeld,
} from '@/features/accounts/attention-labels';
import { formatAccountLabel, type Account } from '@/features/accounts/accounts.api';
import { BANNED_RETRY_LABEL, BannedRetryDialog } from '@/features/accounts/BannedRetryDialog';
import { QrRequiredDialog } from '@/features/accounts/QrRequiredDialog';
import { useReconnectAccount } from '@/features/accounts/useReconnectAccount';
import { useRescanAccount } from '@/features/accounts/useRescanAccount';
import { useAuthStore } from '@/store/auth';
import { cn } from '@/lib/utils';

/**
 * Per-conversation banner — the second half of the 2026-09 disconnect early
 * warning (spec 01 §3.5): "打开该号码的会话时，聊天区顶部提示「此账号当前无法发送」，
 * 管理员可直接点「扫码/重连/重扫」".
 *
 * Mounted between the header and the message list in `ChatPanel.tsx`
 * (single-conversation view) and once PER non-connected member inside
 * `ThreadPanel.tsx`'s "no connected member" branch (replacing the old plain
 * grey notice — spec 01 §7 file table). Self-contained: resolves its own
 * admin gate and mutations, so both call sites just pass the `Account`.
 *
 * Returns `null` when the account is connected (or absent) — callers don't
 * need to gate the render themselves.
 */

export interface ConversationAccountBannerProps {
  account: Account | null | undefined;
}

export function ConversationAccountBanner({
  account,
}: ConversationAccountBannerProps): React.JSX.Element | null {
  const isAdmin = useAuthStore((s) => s.agent?.role) === 'admin';
  const [qrOpen, setQrOpen] = useState(false);
  const [confirmingRescan, setConfirmingRescan] = useState(false);
  const [retryOpen, setRetryOpen] = useState(false);
  const rescan = useRescanAccount();
  const reconnect = useReconnectAccount();

  // The account left `banned` while the retry confirm was open (a poll / WS
  // patch): drop it, so a later ban cannot re-open a stale dialog by itself.
  if (retryOpen && account?.status !== 'banned') setRetryOpen(false);

  if (!account || account.status === 'connected') {
    return null;
  }
  // Narrow into a `const` local: the early return above narrows `account`
  // for the rest of THIS function's linear flow, but the closures below
  // (handleRescanConfirm etc.) are defined after it — binding a `const`
  // keeps the narrowed `Account` type inside them too, with no non-null
  // assertions needed.
  const acc: Account = account;

  const label = formatAccountLabel(acc);
  const reasonText = describeAccountUnavailable(acc, Date.now());
  // A ban HELD by the hub's 403-storm guard was NOT stopped: the number keeps
  // retrying, so the banner says so and asks for an admin now.
  const stormHeld = isBanStormHeld(acc);
  const reason = acc.attention?.reason;

  const canScan = isAdmin && acc.status === 'qr_required';
  // `banned`: 重新扫码 (wipe credentials, re-pair) and 重试连接（保留凭据） —
  // `/reconnect`, which the hub accepts on a banned row as "retry with the
  // preserved credentials" (re-enable + start, no logout), behind a confirm.
  const canRescan = isAdmin && acc.status === 'banned';
  const canRetryBanned = canRescan;
  const canReconnect =
    isAdmin && (reason === 'connecting_stuck' || reason === 'restart_breaker');
  const bannedActionPending = rescan.isPending || reconnect.isPending;

  function handleScanClick(): void {
    setQrOpen(true);
  }

  function handleRescanRequest(e: MouseEvent<HTMLButtonElement>): void {
    e.stopPropagation();
    setConfirmingRescan(true);
  }

  function handleRescanCancel(e: MouseEvent<HTMLButtonElement>): void {
    e.stopPropagation();
    setConfirmingRescan(false);
  }

  function handleRescanConfirm(e: MouseEvent<HTMLButtonElement>): void {
    e.stopPropagation();
    rescan.mutate(acc.id, { onSettled: () => setConfirmingRescan(false) });
  }

  function handleReconnectClick(e: MouseEvent<HTMLButtonElement>): void {
    e.stopPropagation();
    reconnect.mutate(acc.id);
  }

  function handleRetryRequest(e: MouseEvent<HTMLButtonElement>): void {
    e.stopPropagation();
    setRetryOpen(true);
  }

  function handleRetryConfirm(): void {
    // Close on either outcome; a failure (e.g. an older hub's 409 for
    // `banned`) is toasted with the hub's detail by `useReconnectAccount`.
    reconnect.mutate(acc.id, { onSettled: () => setRetryOpen(false) });
  }

  // Prominent, but must never block typing/sending in OTHER healthy
  // conversations (T1-Fb brief) — this is a plain banner row, not a modal or
  // an overlay, and it only mounts for the ONE conversation whose account is
  // affected.
  const tone =
    acc.attention?.level === 'critical'
      ? 'bg-red-100/80 text-red-900 dark:bg-red-950/40 dark:text-red-200'
      : 'bg-amber-100/80 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200';

  return (
    <div
      data-testid="conversation-account-banner"
      data-account-status={acc.status}
      role="status"
      className={cn(
        'flex shrink-0 flex-wrap items-center gap-2 px-6 py-2 text-xs font-bold',
        tone,
      )}
    >
      <AlertTriangle className="size-4 shrink-0" aria-hidden="true" />
      <span className="flex-1">
        此会话的账号 {label} {reasonText || FALLBACK_ATTENTION_LABEL}，暂时无法发送
        {stormHeld &&
          (isAdmin
            ? ' · 号码未被停用，需要管理员立即处理'
            : ` · 号码未被停用，${ESCALATE_TO_ADMIN_SUFFIX}`)}
      </span>

      {isAdmin ? (
        <div className="flex shrink-0 items-center gap-1.5">
          {canScan && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={handleScanClick}
              className="h-6 px-2 text-[11px] font-bold"
            >
              扫码
            </Button>
          )}
          {canRescan && !confirmingRescan && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={bannedActionPending}
              onClick={handleRescanRequest}
              aria-label={`重新扫码 ${label}`}
              className="h-6 px-2 text-[11px] font-bold"
            >
              重新扫码
            </Button>
          )}
          {canRetryBanned && !confirmingRescan && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={bannedActionPending}
              onClick={handleRetryRequest}
              aria-label={`${BANNED_RETRY_LABEL} ${label}`}
              className="h-6 px-2 text-[11px] font-bold"
            >
              {BANNED_RETRY_LABEL}
            </Button>
          )}
          {canRescan && confirmingRescan && (
            <div className="flex shrink-0 items-center gap-1">
              <span className="text-[10px] font-normal">将清除旧登录凭据并重新配对</span>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={rescan.isPending}
                onClick={handleRescanCancel}
                aria-label={`取消重新扫码 ${label}`}
                className="h-6 px-2 text-[11px]"
              >
                取消
              </Button>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                disabled={rescan.isPending}
                onClick={handleRescanConfirm}
                aria-label={`确认重新扫码 ${label}`}
                className="h-6 px-2 text-[11px]"
              >
                确认
              </Button>
            </div>
          )}
          {canReconnect && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={reconnect.isPending}
              onClick={handleReconnectClick}
              aria-label={`重连 ${label}`}
              className="h-6 px-2 text-[11px] font-bold"
            >
              重连
            </Button>
          )}
        </div>
      ) : (
        !stormHeld && <span className="shrink-0 text-[11px] font-normal">请联系管理员处理</span>
      )}

      {canScan && qrOpen && (
        <QrRequiredDialog account={acc} open={qrOpen} onOpenChange={setQrOpen} />
      )}
      {canRetryBanned && (
        <BannedRetryDialog
          open={retryOpen}
          onOpenChange={setRetryOpen}
          accountLabel={label}
          onConfirm={handleRetryConfirm}
          pending={reconnect.isPending}
        />
      )}
    </div>
  );
}
